import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, Archive } from 'lucide-react';
import { messagec, more } from '../../assets/assets';

const ProjectList = ({ title, showMoreOptions = false, projects = [], isLoading = false, onReviewClick }) => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Function to truncate title if it exceeds 20 characters
  const truncateTitle = (title) => {
    return title.length > 20 ? title.substring(0, 20) + '...' : title;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMoreClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleDropdownAction = (action) => {
    console.log(`${action} clicked`);
    setShowDropdown(false);
    // Add your action handlers here
  };

  // Provide a loading state and an empty state
  if (isLoading) {
    return (
      <div className="font-['Poppins'] w-full">
        <h2 className="text-[#174568] dark:text-blue-400 text-[18px] font-medium mb-4">{title}</h2>
        <div className="space-y-4">
          {[1, 2, 3].map((n) => (
            <div key={n} className="bg-white dark:bg-[#1d2026] rounded-[10px] border-[1px] border-[#DDE1E6] dark:border-[#32353c] p-3 flex items-center shadow-sm animate-pulse">
              <div className="h-[57px] w-12 rounded-md bg-gray-200 dark:bg-[#32353c] mr-3 flex-shrink-0"></div>
              <div className="flex-grow mr-2">
                <div className="h-3 bg-gray-200 dark:bg-[#32353c] rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-[#32353c] rounded w-3/4 mb-3"></div>
                <div className="h-3 bg-gray-200 dark:bg-[#32353c] rounded w-1/5"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Use provided projects or empty array
  const projectsToShow = projects || [];
  // Helper to color-code statuses
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
      case 'in-progress':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400';
      case 'pending':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400';
      case 'cancelled':
      case 'disputed':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
      default:
        return 'bg-gray-100 dark:bg-[#1d2026] text-gray-700 dark:text-gray-300';
    }
  };

  return (
    <div className="font-['Poppins'] w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-[#174568] dark:text-blue-400 text-[18px] font-medium">{title}</h2>
      </div>

      <div className="space-y-4">
        {projectsToShow.length === 0 ? (
          <div className="bg-white dark:bg-[#1d2026] rounded-[10px] border-[1px] border-[#DDE1E6] dark:border-[#32353c] p-6 text-center shadow-sm">
            <p className="text-gray-500 dark:text-gray-400 font-medium text-sm">No {title.toLowerCase()} found.</p>
          </div>
        ) : (
          projectsToShow.map(project => {
            const pendingPaymentIndex = project.additionalPayments?.findIndex(p => p.status === 'pending');
            const hasPendingPayment = pendingPaymentIndex !== undefined && pendingPaymentIndex !== -1;

            return (
              <div key={project.id} className="bg-white dark:bg-[#1d2026] rounded-[10px] border-[1px] border-[#DDE1E6] dark:border-[#32353c] p-3 flex flex-col shadow-sm">
                <div className="flex items-center">
                  <div className="h-[30px] md:h-[57px] w-8 md:w-12 rounded-md overflow-hidden mr-3 flex-shrink-0">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="flex-grow mr-2">
                    <div className="flex justify-between items-start mb-1">
                      <p className="text-[10px] md:text-[12px] text-[#174568B2] dark:text-blue-400/70 font-medium leading-none">{project.category}</p>
                      {project.status && (
                        <span className={`text-[8px] px-2 py-0.5 rounded-full font-light capitalize flex-shrink-0 ml-2 ${getStatusColor(project.status)}`}>
                          {project.status}
                        </span>
                      )}
                    </div>
                    <h3 className="text-[10px] md:text-[14px] text-[#174568] dark:text-white leading-tight mt-1">{truncateTitle(project.title)}</h3>

                    <div className="w-full flex justify-between mt-2">
                      <p className="text-[8px] md:text-xs text-[#000000] dark:text-gray-300">{project.price}</p>
                      <div className="flex items-center gap-3">
                        {project.status === 'completed' && !project.isReviewed && (
                          <button
                            onClick={() => onReviewClick?.(project)}
                            className="px-3 py-1 bg-[#ffc107] hover:bg-[#e0a800] text-[#000] text-[10px] font-semibold rounded-md transition-colors"
                          >
                            Give Review
                          </button>
                        )}
                        <button 
                          onClick={() => project.chatId && navigate(`/clients/messages/${project.chatId}`)}
                          className=" text-gray-400 hover:text-[#174568] transition-colors"
                        >
                          <img src={messagec}  className="w-[18px] h-[18px]"/>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {project.additionalPayments && project.additionalPayments.some(p => p.status === 'pending') && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-[#32353c] flex flex-col gap-2">
                    {project.additionalPayments.map((p, idx) => {
                      if (p.status !== 'pending') return null;
                      return (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="text-xs text-orange-600 dark:text-orange-400 font-medium leading-tight">
                            Payment requested: ${p.amount} <br/>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-normal">{p.description}</span>
                          </div>
                          <button
                            onClick={() => navigate(`/payment/order/${project.id}/${idx}`)}
                            className="px-1.5 py-1 bg-green-600 hover:bg-green-700 text-white text-[10px] md:text-[12px] font-medium  rounded-md transition-colors shadow-sm ml-2 shrink-0"
                          >
                            Pay Now
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ProjectList;